export const hexToRgb = (hex: string): [number, number, number] => {
  const results = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return results ? [parseInt(results[1], 16), parseInt(results[2], 16), parseInt(results[3], 16)] : [0, 0, 0];
};

export const withOpacity = (hexColor: string, opacity: number): string =>
  `rgba(${hexToRgb(hexColor).concat(opacity).join(', ')})`;

const neutral100 = '#191919';
const neutral20 = '#EFEEF1';
const white100 = '#ffffff';

const values = {
  neutral10: '#F6F5F8',
  neutral20,
  neutral50: '#949395',
  neutral100,
  neutral120: '#000000',
  orange10: '#fdf3f0',
  orange20: '#fbe0d9',
  orange100: '#ea663f',
  pink20: '#ffdef2',
  pink40: '#FFCDE5',
  pink100: '#FF82BF',
  pink120: '#F261A1',
  yellow20: '#ffefa2',
  yellow100: '#f0cc00',
  lavander100: '#C7C8F7',
  error10: '#FCECED',
  error100: '#e13f48',
  success10: '#E7F6F0',
  success100: '#079c66',
  white100,
};

const helperColors = {
  background: white100,
  border: neutral20,
  disabled: withOpacity(neutral100, 0.7),
  a20neutral100: withOpacity(neutral100, 0.2),
  a10neutral120: withOpacity(values.neutral120, 0.1),
  a7neutral120: withOpacity(values.neutral120, 0.07),
  transparent: 'transparent',
  whiteAlpha50: withOpacity(white100, 0.5),
  whiteAlpha60: withOpacity(white100, 0.6),
  backdrop: withOpacity(neutral20, 0.6),
  skeleton: {
    container: withOpacity(neutral20, 0.4),
    item: neutral20,
  },
};

const colors = {
  ...values,
  ...helperColors,
};

export type Color = keyof typeof values;

type HelperColor = keyof typeof helperColors;

/**
 * We should favor the use of Color type, if possible.
 */
export type AnyColor = Color | HelperColor;

export default colors;

export const gradientValues = {
  angle: 60,
  colors: {
    default: ['#EB8030', colors.orange100, colors.orange100, colors.pink120],
    gradient50: ['#F5BE99', '#F5B39F', '#F5B39F', '#F8B0CB'],
    gradient110: ['#DF7830', '#DF623D', '#DF623D', '#E65D8E'],
    gradient120: ['#D4712D', '#D45D3A', '#D45D3A', '#DA5891'],
  },
};

export type GradientVariant = keyof typeof gradientValues.colors;

/**
 * Returns the most contrasted text color for a given background color.
 * @param hexBgColor Full hexadecimal string background color to get a text color for.
 * @example
 * - getTextColorForBackground('#ffffff') = colors.neutral100.
 * - getTextColorForBackground('#000000') = colors.white100
 * @links
 * https://stackoverflow.com/a/3943023/6593810
 */
export const getTextColorForBackground = (
  hexBgColor: string,
  darkColor: string = colors.neutral100,
  lightColor: string = colors.white100,
): string => {
  const [red, green, blue] = hexToRgb(hexBgColor);
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186 ? darkColor : lightColor;
};

export const horizontalGradientProps = {
  start: { x: 0, y: 0 },
  end: { x: 1, y: 0 },
};

// CSS-angle (deg) to unit-square start/end points for gradient libs that take coords
// Input examples: 0°=up, 90°=left to right, 45°=bottom left to top right
export const angleToStartEnd = (angleDeg: number) => {
  const rad = (angleDeg * Math.PI) / 180;
  const sin = Math.sin(rad);
  const cos = Math.cos(rad);
  return {
    start: { x: 0.5 - sin / 2, y: 0.5 + cos / 2 },
    end: { x: 0.5 + sin / 2, y: 0.5 - cos / 2 },
  };
};
